import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useCategories from "@/hooks/use-categories";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { ColorPicker } from "../ui/color-picker";
import { categoryFormSchema, CategoryFormValues } from "./form.schema";

interface CategoryFormProps {
  onSubmit: (values: CategoryFormValues) => void;
  defaultValues?: Partial<CategoryFormValues>;
  isSubmitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  mode?: "create" | "edit" | "delete";
  onCancel?: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  onSubmit,
  defaultValues,
  isSubmitting = false,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  mode = "create",
  onCancel,
}) => {
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: "",
      color: "#000000",
      ...defaultValues,
    },
  });
  const { masterCategoriesByType } = useCategories();

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Category name"
                  {...field}
                  disabled={mode === "delete"}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Color</FormLabel>
              <FormControl>
                <ColorPicker
                  value={field.value}
                  onChange={field.onChange}
                  className="rounded-md border bg-background p-4 shadow-sm"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="parent"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Parent Category</FormLabel>
              <FormControl>
                <Select
                  value={field.value ? String(field.value) : ""}
                  onValueChange={(val) =>
                    field.onChange(val ? Number(val) : undefined)
                  }
                >
                  <SelectTrigger disabled={mode === "delete"}>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {masterCategoriesByType.map((group) => (
                      <SelectGroup key={group.type.id}>
                        <SelectLabel>{group.type.name}</SelectLabel>
                        {group.categories.map((category) => (
                          <SelectItem
                            key={category.id}
                            value={category.id.toString()}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center w-full">
                              <div
                                className="flex-shrink-0 w-3 h-3 rounded-full mr-2"
                                style={{
                                  backgroundColor: category.color as string,
                                }}
                              />
                              <span className="truncate">{category.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              className="w-full"
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            className="w-full"
            variant={mode === "delete" ? "destructive" : "default"}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="animate-spin mr-2" />}
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CategoryForm;
